import type { ButtonV3Props, GridItemProps } from '@jouzen/ecom-components';
import {
  ButtonV3,
  GridItem,
  IconTagV3,
  LayoutGrid,
  RightArrowIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import SectionHeading from '@/app/components/SectionHeading';

import type { IconTagProps } from '../LandingPageHero/LandingPageIconTag';

export interface LandingPageBlurbProps {
  readonly eyebrow?: MessageKey;
  readonly title: {
    key: MessageKey;
    colEnd?: GridItemProps['colEnd'];
    multiline: boolean;
    indentation?: string;
  };
  readonly description: {
    key: MessageKey;
    colStart?: GridItemProps['colStart'];
    alignment: 'top' | 'center' | 'bottom';
  };
  readonly action?: Omit<ButtonV3Props, 'key' | 'children'> & {
    title: MessageKey;
    dataCy?: string;
    arrow?: boolean;
  };
  readonly iconTag?: IconTagProps;
}

const LandingPageBlurb = (props: LandingPageBlurbProps): JSX.Element => {
  const { eyebrow, title, description, action, iconTag } = props;

  const {
    title: actionTitle,
    dataCy: actionDataCy,
    arrow: actionArrow,
    ...actionButtonProps
  } = action ?? {};

  const mergedTitleColEnd: GridItemProps['colEnd'] = {
    sm: 'main',
    md: 'main',
    lg: 11,
    ...title.colEnd,
  };

  const mergedDescriptionColStart: GridItemProps['colStart'] = {
    sm: 'main',
    md: 'main',
    lg: 14,
    ...description.colStart,
  };

  const t = useTranslations();

  return (
    <div data-cy="landing-page-blurb">
      <LayoutGrid>
        {iconTag && (
          <GridItem
            className="z-10"
            colEnd={mergedTitleColEnd}
            colStart={{ sm: 'main', md: 'main', lg: 'main' }}
          >
            <IconTagV3
              icon={iconTag.icon}
              iconTagText={t(iconTag.iconTagText)}
              variant="transparent-light"
            />
          </GridItem>
        )}
        {eyebrow && (
          <GridItem
            className="z-10"
            colEnd={mergedTitleColEnd}
            colStart={{ sm: 'main', md: 'main', lg: 'main' }}
          >
            <div data-cy="landing-page-blurb-eyebrow">{t(eyebrow)}</div>
          </GridItem>
        )}
        <GridItem
          className="z-10 lg:pt-0"
          colEnd={mergedTitleColEnd}
          colStart={{ sm: 'main', md: 'main', lg: 'main' }}
        >
          <SectionHeading
            animated
            content={title.key}
            emAnimationProps={{
              transition: { duration: 2, type: 'tween', delay: 0.5 },
            }}
            indentation={title.indentation ?? 'sm:pl-20 md:pl-28'}
            multiline={title.multiline}
            typographyProps={{
              className: 'leading-heading tracking-tighter',
              color: 'inherit',
              Element: 'h3',
              variant: 'h2',
              style: {
                lineHeight: '1.1',
              },
            }}
            wrapperAnimationProps={{
              transition: { duration: 1.25, type: 'tween', delay: 0.25 },
            }}
          />
        </GridItem>
        <GridItem
          className={'z-10 py-4 lg:pb-5 lg:pt-2'}
          colEnd={{ sm: 'main' }}
          colStart={mergedDescriptionColStart}
        >
          <div
            className={cx('flex flex-col', {
              'justify-start': description.alignment === 'top',
              'justify-end': description.alignment === 'bottom',
              'justify-center': description.alignment === 'center',
            })}
            data-cy="landing-page-blurb-description-container"
          >
            <TypographyV3
              className="text-wrap"
              color="inherit"
              data-cy="landing-page-blurb-description"
            >
              {t.rich(description.key)}
            </TypographyV3>
            {action && (
              <div data-cy="landing-page-blurb-action" className="z-10 mt-6">
                <ButtonV3
                  variant={actionArrow ? 'body-link' : 'secondary-dark'}
                  data-cy={actionDataCy}
                  {...actionButtonProps}
                >
                  <span
                    className={cx({
                      'flex flex-row items-center gap-2 underline': actionArrow,
                    })}
                  >
                    {t(actionTitle)}
                    {actionArrow && <RightArrowIcon width="18" />}
                  </span>
                </ButtonV3>
              </div>
            )}
          </div>
        </GridItem>
      </LayoutGrid>
    </div>
  );
};

export default LandingPageBlurb;
